import { SkillDetails, SkillUpdate } from "@amoreg/contracts";
import * as Schema from "effect/Schema";
import { resolvePrimaryEnvironmentHttpUrl } from "../environments/primary/target";

export class SkillRequestError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

export async function requestSkillDetails(
  organizationId: string,
  skillId: string,
  signal: AbortSignal,
  update?: SkillUpdate,
): Promise<SkillDetails> {
  const response = await fetch(
    resolvePrimaryEnvironmentHttpUrl("/api/amoreg/skills", {
      org: organizationId,
      skill: skillId,
    }),
    {
      credentials: "include",
      method: update ? "POST" : "GET",
      signal: AbortSignal.any([signal, AbortSignal.timeout(20_000)]),
      ...(update
        ? { headers: { "content-type": "application/json" }, body: JSON.stringify(update) }
        : {}),
    },
  );
  if (!response.ok) {
    const message =
      response.status === 409
        ? "Someone changed this skill while you were editing. Your draft is still here."
        : response.status === 403
          ? update
            ? "You no longer have permission to edit this skill. Your draft is still here."
            : "You no longer have access to this skill."
          : response.status === 404
            ? "This skill is no longer available in this organisation."
            : "Could not load or save this skill. Please try again.";
    throw new SkillRequestError(response.status, message);
  }
  return Schema.decodeUnknownSync(SkillDetails)(await response.json());
}

export interface SkillDraft {
  name: string;
  description: string;
  body: string;
  category: string;
  tags: string;
}

export const skillDraft = (detail: SkillDetails): SkillDraft => ({
  name: detail.name,
  description: detail.description,
  body: detail.body,
  category: detail.category ?? "",
  tags: detail.tags.join(", "),
});

function normalizedSkillDraft(draft: SkillDraft) {
  return {
    name: draft.name.trim(),
    description: draft.description.trim(),
    body: draft.body.trim(),
    category: draft.category.trim() || null,
    tags: [
      ...new Set(
        draft.tags
          .split(",")
          .map((tag) => tag.trim().toLowerCase())
          .filter(Boolean),
      ),
    ],
  };
}

export function skillUpdate(draft: SkillDraft, version: number): SkillUpdate {
  return Schema.decodeUnknownSync(SkillUpdate)({
    ...normalizedSkillDraft(draft),
    expectedVersion: version,
  });
}

export const skillDraftChanged = (draft: SkillDraft, detail: SkillDetails): boolean =>
  JSON.stringify(normalizedSkillDraft(draft)) !==
  JSON.stringify(normalizedSkillDraft(skillDraft(detail)));
